<template>
  <div>
    <!-- Progress overlay during fanout -->
    <div v-if="running" class="progress-overlay">
      <div class="progress-card">
        <h3>{{ runningVerb }} {{ progress.total }} {{ pluralUnit(progress.total) }}…</h3>
        <p class="progress-status">{{ progress.done }} / {{ progress.total }}</p>
        <div class="progress-track">
          <div
            class="progress-fill"
            :style="{ width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%` }"
          ></div>
        </div>
        <p class="progress-counts">
          <span class="success-count">✓ {{ progress.successes.length }}</span>
          <span v-if="progress.failures.length > 0" class="failure-count">
            ✗ {{ progress.failures.length }}
          </span>
        </p>
      </div>
    </div>

    <!-- Result modal after fanout -->
    <div v-if="showResult" class="modal-backdrop" @click.self="$emit('close')">
      <div class="modal-card">
        <header>
          <h3>
            <i :class="result.failures.length === 0 ? 'fas fa-check-circle success' : 'fas fa-exclamation-triangle warn'"></i>
            {{ completeTitle }} complete
          </h3>
          <button class="icon-btn" @click="$emit('close')" aria-label="Close">
            <i class="fas fa-times"></i>
          </button>
        </header>
        <div class="body">
          <p class="result-summary">
            <span class="success-count">{{ result.successes.length }} succeeded</span>
            <template v-if="retryableFailures.length > 0">
              · <span class="failure-count">{{ retryableFailures.length }} failed (retryable)</span>
            </template>
            <template v-if="terminalFailures.length > 0">
              · <span class="invalid-count">{{ terminalFailures.length }} {{ terminalLabel }}</span>
            </template>
          </p>

          <div v-if="retryableFailures.length > 0" class="failure-list">
            <h4>Failures</h4>
            <ul>
              <li v-for="f in retryableFailures" :key="f.item">
                <code>{{ f.item }}</code>: <span class="failure-msg">{{ f.message }}</span>
              </li>
            </ul>
          </div>

          <div v-if="terminalFailures.length > 0" class="invalid-list">
            <!-- Pages can override with bespoke copy (e.g. the defaults "invalid product" note). -->
            <slot name="terminal" :failures="terminalFailures">
              <h4><i class="fas fa-info-circle"></i> {{ terminalLabel }} — won't be retried</h4>
              <ul>
                <li v-for="f in terminalFailures" :key="f.item">
                  <code>{{ f.item }}</code>: <span class="failure-msg">{{ f.message }}</span>
                </li>
              </ul>
            </slot>
          </div>
        </div>
        <footer>
          <button
            v-if="retryableFailures.length > 0"
            class="btn btn-primary"
            @click="$emit('retry')"
          >
            Retry failed ({{ retryableFailures.length }})
          </button>
          <button class="btn btn-secondary" @click="$emit('close')">Close</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Presentational shell for retentionBulkRunnerMixin: the progress overlay shown
 * during a fanout and the result modal shown afterwards. All state is bound in
 * from the mixin host; this component owns no logic beyond pluralisation.
 *
 * The `terminal` slot lets a page replace the generic non-retryable list with
 * bespoke copy (e.g. the defaults pages' "rejected by Apple — invalid product").
 */
export default {
  name: 'RetentionBulkRunnerModals',
  props: {
    running: { type: Boolean, default: false },
    progress: { type: Object, default: () => ({ total: 0, done: 0, successes: [], failures: [] }) },
    showResult: { type: Boolean, default: false },
    result: { type: Object, default: () => ({ successes: [], failures: [] }) },
    unit: { type: String, default: 'item' },
    runningVerb: { type: String, default: 'Processing' },
    completeTitle: { type: String, default: 'Bulk operation' },
    terminalLabel: { type: String, default: 'not retryable' },
    retryableFailures: { type: Array, default: () => [] },
    terminalFailures: { type: Array, default: () => [] }
  },
  methods: {
    pluralUnit(n) {
      return n === 1 ? this.unit : `${this.unit}s`;
    }
  }
};
</script>

<style scoped>
.btn {
  padding: 0.45rem 1rem;
  border-radius: 6px;
  border: none;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}

.btn-primary {
  background: #03B3DA;
  color: #fff;
}
.btn-primary:hover { background: #04a3c5; }

.btn-secondary {
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.18);
}
.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.3);
}

/* Progress overlay */
.progress-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.progress-card {
  background: #1a2332;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 1.5rem 2rem;
  min-width: 360px;
  max-width: 90vw;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  color: rgba(255, 255, 255, 0.88);
}

.progress-card h3 {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  color: #fff;
  font-weight: 600;
}

.progress-status {
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.7);
  font-variant-numeric: tabular-nums;
  margin: 0 0 0.4rem;
}

.progress-track {
  height: 8px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: #03B3DA;
  border-radius: 999px;
  transition: width 0.18s ease;
}

.progress-counts {
  display: flex;
  gap: 1rem;
  font-size: 0.85rem;
  margin: 0;
}

.success-count { color: #2ecc71; }
.failure-count { color: #e74c3c; }

/* Result modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  animation: fade 0.15s ease;
  backdrop-filter: blur(4px);
}

@keyframes fade {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.modal-card {
  width: min(560px, calc(100vw - 2rem));
  background: #1a2332;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  color: rgba(255, 255, 255, 0.88);
}

.modal-card header,
.modal-card footer {
  display: flex;
  align-items: center;
  padding: 0.85rem 1.25rem;
  background: rgba(255, 255, 255, 0.03);
}

.modal-card header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modal-card footer {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  justify-content: flex-end;
  gap: 0.5rem;
}

.modal-card header h3 {
  margin: 0;
  font-size: 1.02rem;
  color: #fff;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
}

.modal-card header h3 .success { color: #2ecc71; }
.modal-card header h3 .warn { color: #ffc832; }

.icon-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  padding: 0.25rem 0.4rem;
}

.icon-btn:hover { color: #fff; }

.body {
  padding: 1.25rem;
  overflow-y: auto;
}

.result-summary {
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.75);
  margin: 0 0 1rem;
}

.result-summary .success-count { color: #2ecc71; }
.result-summary .failure-count { color: #e74c3c; }
.result-summary .invalid-count { color: #ffc832; }

.failure-list h4 {
  margin: 0 0 0.5rem;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #e74c3c;
}

.failure-list ul {
  margin: 0;
  padding-left: 1rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
}

.failure-list li { margin-bottom: 0.25rem; }

.failure-list code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
  background: rgba(255, 255, 255, 0.06);
  padding: 1px 5px;
  border-radius: 3px;
  color: #fff;
}

.failure-msg { color: rgba(255, 255, 255, 0.7); }

.invalid-list {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 192, 50, 0.06);
  border: 1px solid rgba(255, 192, 50, 0.22);
  border-radius: 6px;
}

.invalid-list ::v-deep h4 {
  margin: 0 0 0.4rem;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #ffc832;
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.invalid-list ::v-deep .info-text {
  margin: 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.5;
}

.invalid-list ::v-deep ul {
  margin: 0.4rem 0 0;
  padding-left: 1rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
}

.invalid-list ::v-deep li { margin-bottom: 0.25rem; }

.invalid-list ::v-deep code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
  background: rgba(255, 255, 255, 0.08);
  padding: 1px 6px;
  border-radius: 3px;
  color: #fff;
}
</style>
